import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpService } from '@nebula-shared/data-access';
import { finalize } from 'rxjs/operators';

/** Estados de la pantalla pública de recuperación de contraseña. */
type RecuperarEstado = 'formulario' | 'enviando' | 'listo';

/**
 * Página PÚBLICA de recuperación de contraseña ("olvidé mi clave"). El usuario ingresa su username o
 * correo; se solicita a nebula-masters el envío de un enlace de reinicio. Por anti-enumeración la
 * respuesta es genérica: nunca revela si la cuenta existe. Al abrir el enlace del correo, el usuario
 * define su nueva contraseña en la página de activación (reutilizada).
 */
@Component({
  selector: 'nebula-recuperar',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './recuperar.component.html',
  styleUrls: ['./recuperar.component.scss'],
})
export class RecuperarComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly http = inject(HttpService);

  /** Endpoint público de solicitud de reinicio en nebula-masters. */
  private static readonly SOLICITAR = '/nebula-masters/api/v1/usuario/activacion/solicitar';

  /** Estado de la pantalla. */
  readonly estado = signal<RecuperarEstado>('formulario');

  /** Formulario con el identificador (username o correo). */
  readonly form: FormGroup = this.fb.group({
    identificador: ['', [Validators.required]],
  });

  /** Solicita el enlace de reinicio. Anti-enumeración: mismo resultado exista o no la cuenta. */
  enviar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.estado.set('enviando');
    this.http
      .post<void>(RecuperarComponent.SOLICITAR, { identificador: this.form.value.identificador })
      .pipe(finalize(() => this.form.enable()))
      .subscribe({
        next: () => this.estado.set('listo'),
        error: () => this.estado.set('listo'),
      });
  }

  /** Ir al login. */
  irAlLogin(): void {
    this.router.navigate(['/login']);
  }
}
